import { CustomNotification } from 'features/settings/notification/types/hubstr';
import { v4 as uuidv4 } from 'uuid';
import { Department, ResidentList } from 'features/residents/types/hubstr';
import { Event } from 'features/events/types/hubstr';
import { NOTIFICATION_STATUS } from 'features/settings/notification/constants';
import { translate, useTranslate } from 'magner';
import { request } from '~/utils/request';

interface Pager {
  currentPage: number,
  totalPages: number,
  totalItems: number,
}

interface IAutocomplete {
  id: string,
  title: string,
}

const BASE_URL = '/api/admin/notification/';

export const notificationRead = request.table<CustomNotification[]>(async ({ api, data }) => {
  const pagination = `count=${data.pagination.items || 25}&page=${data.pagination?.page || 1}`;
  let query = '';
  if (Object.keys(data.sort).length) {
    Object.keys(data.sort).map((s, index) => {
      if (data.sort[s]) {
        query += `&sort[${index}][id]=${s}&sort[${index}][value]=${data.sort[s]}`;
      }

      return s;
    });
  } else {
    query = '&sort[0][id]=notifyAt&sort[0][value]=DESC';
  }

  const res = await api.get<{ list: CustomNotification[], pager: Pager }>(`${BASE_URL}list?${pagination}${query}`);
  if (res.error) {
    return {
      data: {
        rows: [],
        pagination: null,
      },
    };
  }

  return {
    data: {
      rows: res.data.list,
      pagination: {
        currentPage: res.data.pager?.currentPage || 1,
        totalPages: res.data.pager?.totalPages || 1,
        totalItems: res.data.pager?.totalItems || 0,
      },
    },
  };
});
/**
 * Создать рассылку
 */
export const notificationCreate = request.card<Partial<CustomNotification>>(async ({
  api, parseError, data, router,
}) => {
  const id = uuidv4();

  const body: Partial<CustomNotification> = {
    id,
    caption: data.data.caption,
    text: data.data.text,
    userListType: data.data.userListType,
    notifyAt: data.data.notifyAt,
    status: data.data.draft.length ? 'draft' : 'planned',
  };

  if (data.data.users && data.data.users.length) {
    body.users = data.data.users;
  }

  if (data.data.groupId && data.data.groupId.length) {
    body.groupId = data.data.groupId;
  }

  if (data.data.deeplinkTemplate) {
    body.deeplinkTemplate = data.data.deeplinkTemplate;

    if (!Array.isArray(data.data.deeplinkEntityId)) {
      body.deeplinkEntityId = data.data.deeplinkEntityId;
    }
  }

  body.departments = data.data.departments || [];

  const res = await api.post(`${BASE_URL}create`, body);
  if (res.error) {
    return { error: parseError?.(res.error) };
  }

  await router.push({ name: 'notifications' });
  return { data: res.data?.user };
});
/**
 * Получить значения по умолчанию для новой рассылки
 */
export const notificationNewGet = request.card<Partial<CustomNotification>>(async ({ api, data, parseError }) => ({
  data: {
    userListType: 'all',
  },
}));
/**
 * Получить созданную рассылку
 */
export const notificationGet = request.card<CustomNotification>(async ({ api, data, parseError }) => {
  const { customT } = useTranslate();
  const res = await api.get<{data: CustomNotification }>(`${BASE_URL}show/${data.id}`);
  if (res.error) {
    return { error: parseError(res.error) };
  }

  return {
    data: {
      ...res.data.data,
      id: data.id,
      users: res.data.data.users.map((u: {id: string, name: string}) => ({
        id: u.id,
        label: u.name,
      })),
      deeplinkEntityId: res.data?.data.deeplinkEntity,
      draft: res.data.data.status === 'draft' ? [true] : [],
      statusText: customT(translate(NOTIFICATION_STATUS[res.data.data.status].text)),
      notifyAt: new Date(`${res.data.data.notifyAt.date}Z`),
      createAt: new Date(`${res.data.data.createAt.date}Z`),
    },
  };
});
/**
 * Обновить рассылку
 */
export const notificationUpdate = request.card<Partial<CustomNotification>>(async ({
  api, parseError, data, router,
}) => {
  console.log(data.data);
  const body: Partial<CustomNotification> = {
    id: data.data.id,
    caption: data.data.caption,
    text: data.data.text,
    userListType: data.data.userListType,
    notifyAt: data.data.notifyAt,
    status: data.data.draft.length ? 'draft' : 'planned',
  };

  if (data.data.users && data.data.users.length) {
    body.users = data.data.users.map((user: string | { id: string, label: string }) => {
      if (typeof user === 'object') {
        return user.id;
      }
      return user;
    });
  }

  if (data.data.groupId && data.data.groupId.length) {
    body.groupId = data.data.groupId;
  }

  if (data.data.deeplinkTemplate && data.data.deeplinkTemplate.length) {
    body.deeplinkTemplate = data.data.deeplinkTemplate;

    if (typeof data.data.deeplinkEntityId === 'object') {
      body.deeplinkEntityId = data.data.deeplinkEntityId.id;
    } else if (!Array.isArray(data.data.deeplinkEntityId)) {
      body.deeplinkEntityId = data.data.deeplinkEntityId;
    }
  }

  const res = await api.patch(`${BASE_URL}update`, body);
  if (res.error) {
    return { error: parseError?.(res.error) };
  }

  await router.push({ name: 'notifications' });
  return { data: res.data?.user };
});
/**
 * Удалить рассылку
 */
export const notificationDelete = request.card(async ({
  api, data, parseError, router,
}) => {
  const res = await api.delete(`${BASE_URL}delete?id=${data.id}`);
  if (res.error) {
    return { error: parseError(res.error) };
  }

  await router.push({ name: 'notifications' });
  return { data: 'ok' };
});
/**
 * Список шаблонов для диплинков
 */
export const deepLinkList = request.table<any>(async ({ api, data }) => {
  const res = await api.get<any>('/api/admin/link/templates');
  if (res.error) {
    return {
      data: {
        rows: [],
      },
    };
  }

  return {
    data: {
      rows: res.data.list,
    },
  };
});
/**
 * Поиск резидентов по имени
 */
export const searchResidents = request.table<IAutocomplete>(async ({ api, data }) => {
  const search = data.search ? `&search=${data.search}` : '';

  const res = await api.get<{ data: { list: IAutocomplete[] }, pager: Pager }>(
    `/api/admin/user/autocomplete?count=100${search}`,
  );
  if (res.error) {
    return {
      data: {
        rows: [],
      },
    };
  }

  return {
    data: {
      rows: res.data?.data.list,
    },
  };
});

export const searchDepartment = request.table<IAutocomplete>(async ({ api, data }) => {
  const search = data.search ? `&search=${data.search}` : '';

  const res = await api.get<{ departments: Department[], pager: Pager }>(
    `/api/department/autocomplete?count=100${search}`,
  );
  if (res.error) {
    return {
      data: {
        rows: [],
      },
    };
  }

  return {
    data: {
      rows: res.data?.departments,
    },
  };
});
/**
 * Поиск мероприятий
 */
export const searchEvents = request.table<IAutocomplete>(async ({ api, data }) => {
  const search = data.search ? `&search=${data.search}` : '';

  const res = await api.get<{ data: { list: IAutocomplete[] }, pager: Pager }>(
    `/api/admin/event/autocomplete?count=1000${search}`,
  );

  if (res.error) {
    return {
      data: {
        rows: [],
        pagination: null,
      },
    };
  }

  return {
    data: {
      rows: res.data?.data.list,
    },
  };
});
/**
 *
 */
export const searchPosts = request.table<IAutocomplete>(async ({ api, data }) => {
  const search = data.search ? `&search=${data.search}` : '';

  const res = await api.get<{ data: { list: IAutocomplete[] }, pager: Pager }>(
    `/api/admin/post/autocomplete?count=200${search}`,
  );

  if (res.error) {
    return {
      data: {
        rows: [],
        pagination: null,
      },
    };
  }

  return {
    data: {
      rows: res.data?.data.list,
    },
  };
});
/**
 *
 */
export const searchPositionTags = request.table<any>(async ({ api, data }) => {
  const search = data.search ? `&filters[0][id]=search&filters[0][value]=${data.search}` : '';

  const res = await api.get<{ list: Event[], pager: Pager }>(
    `/api/admin/user/position/list?count=200&offset=0${search}`,
  );

  if (res.error) {
    return {
      data: {
        rows: [],
        pagination: null,
      },
    };
  }

  return {
    data: {
      rows: res.data.list,
    },
  };
});
